import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Column } from '@material-table/core';
import DataTable from '../../components/shared/DataTable/DataTable';
import Badge from '../../components/shared/Badge/Badge';
import BtButton from '../bt-button/bt-button';
import { RequestStatus } from '../../services/service-evaluations';
import { EvaluationRecipientStatus, EvaluationRequestStatus } from '../../util/evaluationContract';
import { formatScore } from '../../util/evaluationFormat';
import { recipientStatusMeta, requestStatusMeta } from '../../util/evaluationStatusMeta';
import { formatStockholmDay } from './reviewFormat';
import styles from './page-supplier-evaluations.module.scss';

export type RecipientRow = RequestStatus['recipients'][number];

type Props = {
  recipients: RecipientRow[];
  onShowAnswer: (responseId: string) => void;
};

const LATE_STYLE = requestStatusMeta(EvaluationRequestStatus.Overdue);

const TABULAR = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const;

const dayOrDash = (instant: string | null) => (instant ? formatStockholmDay(instant) : '–');

const RecipientTable: FC<Props> = ({ recipients, onShowAnswer }) => {
  const [t] = useTranslation('evaluations');

  const columns: Column<RecipientRow>[] = useMemo(
    () => [
      {
        title: t('status.recipients.recipient', 'Mottagare'),
        field: 'name',
        render: recipient => (
          <div className={styles.recipient}>
            <span className={styles.author}>{recipient.name || recipient.email}</span>
            {recipient.title && <span className={styles.muted}>{recipient.title}</span>}
            {recipient.email && recipient.name && (
              <span className={styles.muted}>{recipient.email}</span>
            )}
          </div>
        ),
      },
      {
        title: t('status.recipients.status', 'Status'),
        field: 'status',
        width: 170,
        render: recipient => {
          const meta = recipientStatusMeta(recipient.status);
          const declinedOn =
            recipient.status === EvaluationRecipientStatus.Declined && recipient.declinedAt;
          return (
            <div className={styles.recipient}>
              <Badge color={meta.color} backgroundColor={meta.backgroundColor}>
                {t(meta.labelKey, meta.fallback)}
              </Badge>
              {declinedOn && (
                <span className={`${styles.small} ${styles.muted}`}>
                  {formatStockholmDay(declinedOn)}
                </span>
              )}
            </div>
          );
        },
      },
      {
        title: t('status.recipients.sent', 'Skickad'),
        field: 'sentAt',
        width: 120,
        cellStyle: TABULAR,
        render: recipient => dayOrDash(recipient.sentAt),
      },
      {
        title: t('status.recipients.answered', 'Besvarad'),
        field: 'completedAt',
        width: 120,
        cellStyle: TABULAR,
        render: recipient => dayOrDash(recipient.completedAt),
      },
      {
        title: t('status.recipients.score', 'Snitt'),
        field: 'score',
        width: 120,
        render: recipient =>
          recipient.responseId ? (
            <span className={styles.question}>
              <span className={styles.barValue}>{formatScore(recipient.score)}</span>
              {recipient.late && (
                <Badge color={LATE_STYLE.color} backgroundColor={LATE_STYLE.backgroundColor}>
                  {t('response.late', 'Sen')}
                </Badge>
              )}
            </span>
          ) : (
            '–'
          ),
      },
      {
        title: '',
        field: 'responseId',
        width: 110,
        align: 'right',
        sorting: false,
        render: ({ responseId }) =>
          responseId && (
            <BtButton size="xxs" color="white" onClick={() => onShowAnswer(responseId)}>
              {t('review.comments.showAnswer', 'Visa svar')}
            </BtButton>
          ),
      },
    ],
    [t, onShowAnswer],
  );

  const rows = useMemo(() => recipients.map(recipient => ({ ...recipient })), [recipients]);

  return <DataTable<RecipientRow> isLoading={false} columns={columns} data={rows} />;
};

export default RecipientTable;
